define("Widgets/ScrollContent2", [`Controller`, `module`], (Super, Self) => {
	return Super.inherit({
		namespace: Self.id,
		defaults: {
			loadStylesheet: true,
			arrowsForDesktop: true,
			arrowMode: `edges`, // edges, below, edges-circled
			startIndex: 0, // index of the item to start scrolling at

			/**
			 * callbacks: {
			 * 	 onIndexChange: function({index, displayLeftTrigger, displayRightTrigger}) { ... },
			 * }
			 */
		},
		requires: [`App`],
		constructor(setup) {
			const { data, elements } = this;
			const { arrowsForDesktop, arrowMode, startIndex } = setup;

			this.addClass(`arrow-style-${arrowMode}`);

			this.assign(data, {
				arrowsForDesktop,
				startIndex,
			});

			elements.list = this.get(`ul`);
			elements.items = this.select(`ul > li`);

			if (arrowsForDesktop) {
				this.createArrows();
			}

			if (arrowsForDesktop || this.isFunction(setup.callbacks.onIndexChange)) {
				this.updateScrollArrows();
			}

			if (startIndex > 0 && this.isArray(elements.items, startIndex + 1)) {
				this.scrollByDistanceToIndex({ index: startIndex, behavior: `instant` });
			}
		},
		api: {
			createArrows: {
				value() {
					const arrowContainer = document.createElement(`div`);
					const leftArrowElement = this.createLeftArrow();
					const rightArrowElement = this.createRightArrow();

					this.addClass(`arrow-container`, arrowContainer);
					this.container.appendChild(arrowContainer);

					arrowContainer.appendChild(leftArrowElement);
					arrowContainer.appendChild(rightArrowElement);

					this.hide({ target: leftArrowElement });
					this.hide({ target: rightArrowElement });
					this.hide({ target: arrowContainer });
				},
			},

			createLeftArrow: {
				value() {
					const leftArrowElement = document.createElement(`a`);

					leftArrowElement.setAttribute(`href`, `#`);
					leftArrowElement.setAttribute(`action`, `scroll-left`);
					this.addClass(`hide-mobile`, leftArrowElement);
					leftArrowElement.setAttribute(`aria-label`, `scroll left`);
					leftArrowElement.innerHTML = this.Templates.getIcon(`arrow-left`);

					return leftArrowElement;
				},
			},

			createRightArrow: {
				value() {
					const rightArrowElement = document.createElement(`a`);

					rightArrowElement.setAttribute(`href`, `#`);
					rightArrowElement.setAttribute(`action`, `scroll-right`);
					this.addClass(`hide-mobile`, rightArrowElement);
					rightArrowElement.setAttribute(`aria-label`, `scroll right`);
					rightArrowElement.innerHTML = this.Templates.getIcon(`arrow-right`);

					return rightArrowElement;
				},
			},

			checkForUpdates: {
				public: true,
				value(context, options = { skipCallbacks: false }) {
					const { elements } = this;
					elements.items = this.select(`ul > li`);
					this.updateScrollArrows(options);
				},
			},

			updateScrollArrows: {
				debounce: 200,
				value(options = { skipCallbacks: false }) {
					const leftArrowElement = this.get(`[action="scroll-left"]`);
					const rightArrowElement = this.get(`[action="scroll-right"]`);
					const arrowContainer = this.get(`.arrow-container`);

					if (this.isArray(this.elements.items, 1)) {
						const { DOM, callbacks, data, elements } = this;
						const { arrowsForDesktop } = data;
						const { items, list } = elements;
						const disabledClass = `disabled`;
						const firstElement = items[0];
						const lastElement = items[items.length - 1];
						const epsilon = 1;
						let shouldShowLeftArrow = false;
						let showRightArrow = false;

						DOM.deferUICalculations(list, () => {
							const { IntersectionObserver } = window;
							// Dimension reads would return zeros and corrupt arrow state — instead,
							// register a one-shot IntersectionObserver so the update runs the moment
							// the container actually becomes visible.
							if (!list.offsetParent) {
								// Disconnect any previously pending observer before creating a new one.
								if (this.instances.visibilityObserver) {
									this.instances.visibilityObserver.disconnect();
								}

								this.instances.visibilityObserver = new IntersectionObserver((entries) => {
									if (entries[0].isIntersecting) {
										this.instances.visibilityObserver.disconnect();
										this.instances.visibilityObserver = null;
										this.updateScrollArrows(options);
									}
								});

								this.instances.visibilityObserver.observe(list);

								return;
							}

							const listDimensions = DOM.getDimensions(list);

							shouldShowLeftArrow =
								listDimensions.left + listDimensions.paddingLeft - DOM.getDimensions(firstElement).left > epsilon;

							if (
								DOM.getDimensions(lastElement).right - (listDimensions.right - listDimensions.paddingRight) >
								epsilon
							) {
								showRightArrow = true;
							}

							if (arrowsForDesktop) {
								if (shouldShowLeftArrow || showRightArrow) {
									this.show({ target: leftArrowElement });
									this.show({ target: rightArrowElement });
									this.show({ target: arrowContainer });
								} else {
									this.hide({ target: leftArrowElement });
									this.hide({ target: rightArrowElement });
									this.hide({ target: arrowContainer });
								}

								if (shouldShowLeftArrow) {
									this.removeClass(disabledClass, leftArrowElement);
								} else {
									this.addClass(disabledClass, leftArrowElement);
								}

								if (showRightArrow) {
									this.removeClass(disabledClass, rightArrowElement);
								} else {
									this.addClass(disabledClass, rightArrowElement);
								}
							}

							let closestElementIndex = 0;
							let closestElementDistance = Infinity;
							// find centered most element index
							this.each(items, (item, index) => {
								const itemDimensions = DOM.getDimensions(item);
								const itemCenter = itemDimensions.left + itemDimensions.width / 2;

								// get device center x
								const deviceCenter = window.innerWidth / 2;
								const distance = Math.abs(itemCenter - deviceCenter);

								if (distance < closestElementDistance) {
									closestElementIndex = index;
									closestElementDistance = distance;
								}
							});

							if (!options.skipCallbacks && this.isFunction(callbacks.onIndexChange)) {
								callbacks.onIndexChange({
									index: closestElementIndex,
									displayLeftTrigger: shouldShowLeftArrow,
									displayRightTrigger: showRightArrow,
								});
							}
						});
					} else {
						this.hide({ target: leftArrowElement });
						this.hide({ target: rightArrowElement });
						this.hide({ target: arrowContainer });
					}
				},
			},

			scrollLeft: {
				public: true,
				value() {
					const { DOM, callbacks, elements } = this;
					const { items, list } = elements;
					const listDimensions = DOM.getDimensions(list);
					const epsilon = 1;
					let cutoffElement = null;
					let cutoffElementDimensions = null;
					let isPotentialCutoffElement = null;
					let cutoffElementIndex = -1;
					let getScrollAmount = null;

					isPotentialCutoffElement = (item) => {
						return DOM.getDimensions(item).left + epsilon < listDimensions.left + listDimensions.paddingLeft;
					};

					getScrollAmount = (cutoffElementDimensions) => {
						return cutoffElementDimensions.right - listDimensions.right + listDimensions.paddingRight;
					};

					this.each(items, (item, index) => {
						if (this.isFunction(isPotentialCutoffElement)) {
							if (isPotentialCutoffElement(item)) {
								cutoffElement = item;
								cutoffElementIndex = index;
								cutoffElementDimensions = DOM.getDimensions(cutoffElement);
							}
						}
					});

					if (cutoffElement != null) {
						if (this.isFunction(getScrollAmount)) {
							list.scrollBy(getScrollAmount(cutoffElementDimensions), 0);
						}

						if (this.isFunction(callbacks.onItemSelected) && cutoffElementIndex >= 0) {
							callbacks.onItemSelected(cutoffElementIndex);
						}
					}
				},
			},
			scrollRight: {
				public: true,
				value() {
					const { DOM, callbacks, elements } = this;
					const { items, list } = elements;
					const listDimensions = DOM.getDimensions(list);
					let cutoffElement = null;
					const epsilon = 1;
					let cutoffElementIndex = -1;

					this.each(items, (item, index) => {
						if (
							listDimensions.right - listDimensions.paddingRight < DOM.getDimensions(item).right - epsilon &&
							cutoffElement == null
						) {
							cutoffElement = item;
							cutoffElementIndex = index;
						}
					});

					if (cutoffElement != null) {
						list.scrollBy(DOM.getDimensions(cutoffElement).left - listDimensions.left - listDimensions.paddingLeft, 0);

						if (this.isFunction(callbacks.onItemSelected) && cutoffElementIndex >= 0) {
							callbacks.onItemSelected(cutoffElementIndex);
						}
					}
				},
			},

			scrollByDistanceToIndex: {
				public: true,
				value(context, { index, behavior = `smooth` }) {
					const { DOM, elements } = this;
					const { items, list } = elements;
					const listDimensions = DOM.getDimensions(list);

					list.scrollBy({
						left: DOM.getDimensions(items[index]).left - listDimensions.left - listDimensions.paddingLeft,
						top: 0,
						behavior,
					});
				},
			},

			findNextElement: {
				public: true,
				value() {
					this.scrollRight();
				},
			},
		},

		destructor() {
			if (this.instances.visibilityObserver) {
				this.instances.visibilityObserver.disconnect();
				this.instances.visibilityObserver = null;
			}
		},

		listeners: [
			{
				event: `click`,
				filter: `[action]`,
				handler(evt) {
					const { currentTarget } = evt;
					const action = currentTarget.getAttribute(`action`);

					switch (action) {
						case `scroll-left`:
							this.scrollLeft();
							break;
						case `scroll-right`:
							this.scrollRight();
							break;
						default:
							break;
					}
				},
			},
			{
				event: `scroll`,
				target: `ul`,
				handler() {
					this.updateScrollArrows();
				},
			},
			{
				event: `resize`,
				target: window,
				debounce: 200, // don't fire too often
				handler() {
					this.updateScrollArrows();
				},
			},
		],
	});
});

//# sourceMappingURL=ScrollContent2.js.map